import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';

/* Custom footer for the Franchise Application page. Neon-green accented,
   three-column layout on desktop, single column on mobile. Uses the local
   FA_GREEN color token (#39FF14). */
const FA_GREEN = '#39FF14';

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.828l-5.35-6.99L4.5 22H1.243l8.028-9.17L1 2h6.914l4.83 6.393L18.244 2Zm-2.4 18h1.86L7.24 4H5.24l10.605 16Z" />
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20 8.5a6.5 6.5 0 0 1-4-1.35V15a5 5 0 1 1-5-5v3.2a1.8 1.8 0 1 0 1.8 1.8V2h3a4.5 4.5 0 0 0 4.2 4.2V8.5Z" />
  </svg>
);

const socials = [
  { name: 'Facebook',  icon: Facebook,   href: 'https://www.facebook.com/ionicestatesales' },
  { name: 'Instagram', icon: Instagram,  href: 'https://www.instagram.com/ionicestatesales' },
  { name: 'X',         icon: XIcon,      href: 'https://www.x.com/ionicestatesale' },
  { name: 'YouTube',   icon: Youtube,    href: 'https://www.youtube.com/@IONICESTATESALES' },
  { name: 'TikTok',    icon: TikTokIcon, href: 'https://www.tiktok.com/@ionicestatesales' },
];

const FranchiseApplyFooter = () => (
  <footer
    data-testid="franchise-apply-footer"
    className="bg-black border-t border-white/10 px-5 md:px-10 py-10 md:py-14"
  >
    <div className="max-w-[1400px] mx-auto grid gap-10 md:gap-14 md:grid-cols-3">
      {/* LEFT COLUMN — brand + description + tagline */}
      <div>
        <img
          src="/franchise/franchise-logo.webp?v=2"
          alt="Ionic Estate Sales Franchise"
          className="h-[54px] md:h-[64px] w-auto mb-5 select-none"
          draggable={false}
        />
        <p className="text-white/75 text-[14px] leading-[1.6] max-w-[380px]">
          Join a proven leader in the estate sale industry. Ionic Estate Sales offers a franchise opportunity built on trust, experience, and real support.
        </p>
        <p
          className="mt-5 font-display uppercase tracking-[0.10em] text-[13px] md:text-[14px]"
          style={{ color: FA_GREEN, textShadow: `0 0 12px ${FA_GREEN}55` }}
        >
          Real Opportunity. Real Support. Real Success.
        </p>
        <p className="mt-6 text-white/50 text-[12px]">
          © 2026 Ionic Estate Sales Franchise. All Rights Reserved.
        </p>
      </div>

      {/* CENTER COLUMN — quote + stars + contact */}
      <div className="text-left md:text-center">
        <blockquote
          className="font-display italic text-white text-[18px] md:text-[19px] leading-[1.35] max-w-[420px] md:mx-auto uppercase tracking-[0.01em]"
          style={{ letterSpacing: '0.005em' }}
        >
          <span
            aria-hidden="true"
            className="align-middle mr-1"
            style={{ color: FA_GREEN, fontSize: '1.6em', fontStyle: 'normal', lineHeight: 0 }}
          >
            &ldquo;
          </span>
          Built on trust. Backed by experience. Focused on your success.
          <span
            aria-hidden="true"
            className="align-middle ml-1"
            style={{ color: FA_GREEN, fontSize: '1.6em', fontStyle: 'normal', lineHeight: 0 }}
          >
            &rdquo;
          </span>
        </blockquote>

        <div className="mt-4 flex md:justify-center gap-1.5" aria-label="Five star rating">
          {[0, 1, 2, 3, 4].map((i) => (
            <svg key={i} viewBox="0 0 24 24" fill={FA_GREEN} className="w-4 h-4" style={{ filter: `drop-shadow(0 0 6px ${FA_GREEN}88)` }}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
            </svg>
          ))}
        </div>

        <div className="mt-6 space-y-2 text-[13.5px] text-white/85 md:flex md:flex-wrap md:justify-center md:gap-x-6 md:gap-y-2 md:space-y-0">
          <a href="tel:5419414830" data-testid="footer-phone" className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="w-4 h-4" style={{ color: FA_GREEN }} /> 541-941-4830
          </a>
          <a href="mailto:ionicteam@ionicestatesales.com" data-testid="footer-email" className="flex items-center gap-2 hover:text-white transition-colors uppercase tracking-wide">
            <Mail className="w-4 h-4" style={{ color: FA_GREEN }} /> ionicteam@ionicestatesales.com
          </a>
          <div className="flex items-center gap-2 uppercase tracking-wide">
            <MapPin className="w-4 h-4" style={{ color: FA_GREEN }} /> Southern Oregon
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN — Follow us + partner mark */}
      <div className="md:text-right">
        <div
          className="font-display uppercase tracking-[0.24em] text-[12px] mb-4"
          style={{ color: FA_GREEN }}
        >
          Follow Us
        </div>
        <div className="flex md:justify-end gap-3">
          {socials.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.name}
                href={s.href}
                aria-label={s.name}
                data-testid={`social-${s.name.toLowerCase()}`}
                target={s.href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer noopener"
                className="inline-grid place-items-center w-10 h-10 rounded-full border transition-colors"
                style={{ borderColor: `${FA_GREEN}66`, color: FA_GREEN, background: 'rgba(57,255,20,0.06)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = FA_GREEN; e.currentTarget.style.color = '#000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(57,255,20,0.06)'; e.currentTarget.style.color = FA_GREEN; }}
              >
                <Icon className="w-4 h-4" />
              </a>
            );
          })}
        </div>

        <div className="mt-5 flex md:justify-end gap-5 text-[12px] text-white/50">
          <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-white transition-colors">Terms of Use</a>
        </div>
      </div>
    </div>
  </footer>
);

export default FranchiseApplyFooter;
